import React, { useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  Paper,
  Fab,
  alpha,
  Alert,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";
import {
  docKindFor,
  displayFileName,
  FileTypeIcon,
  Icon as UiIcon,
} from "../ui/icons";
import DuplicateNameDialog from "../HistoryDrawer/DuplicateNameDialog";
import { RADIUS } from "../../theme/mixins";
export type FileType = "pdf" | "doc" | "excel";

interface DocumentsUploadProps {
  heading: string;
  middleContent?: ReactNode;
  buttonText: string;
  buttonStartIcon?: ReactNode;
  buttonEndIcon?: ReactNode;
  filetype?: FileType;
  fileName?: string;
  fileSize?: string;
  fileUploadDate?: string;
  onClick?: () => void;
  onFileUpload?: (file: File) => void;
  onFileRemove?: () => void;
  disabled?: boolean;
  maxFileSizeMB?: number; // Default 20MB
  onFileSizeError?: (file: File, maxSizeMB: number) => void;
  // Duplicate name handling
  existingFileNames?: string[]; // List of existing file names to check against
  onDuplicateName?: (
    fileName: string,
    onReplace: () => void,
    onContinue: () => void,
    onRename: (newName: string) => void,
  ) => void;
  showButton?: boolean; // Control button visibility
  showDocuments?: boolean; // Control documents section visibility (file upload and file display)
  allowedFileExtensions?: string[]; // e.g., ['.pdf', '.docx', '.xlsx']
  isCompanySelected?: boolean;
  isConversionTypeSelected?: boolean;
  onValidationError?: (message: string) => void;
}

const DocumentsUpload: React.FC<DocumentsUploadProps> = ({
  heading,
  middleContent,
  buttonText = "",
  buttonStartIcon,
  buttonEndIcon,
  filetype,
  fileName,
  fileSize,
  fileUploadDate,
  onClick,
  onFileUpload,
  onFileRemove,
  disabled = false,
  maxFileSizeMB = 20,
  onFileSizeError,
  existingFileNames = [],
  onDuplicateName,
  showButton = true,
  showDocuments = true,
  allowedFileExtensions = [".pdf", ".docx", ".xlsx"],
  isCompanySelected = true,
  isConversionTypeSelected = true,
  onValidationError,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const fileLabel = filetype?.toUpperCase();
  const hasFile = fileName && fileSize && fileUploadDate && filetype;
  const [duplicateNameDialogOpen, setDuplicateNameDialogOpen] = useState(false);
  const validateFileSize = (file: File): boolean => {
    const fileSizeInMB = file.size / (1024 * 1024);

    // Clear any existing errors
    setError(null);

    if (fileSizeInMB > maxFileSizeMB) {
      const errorMessage = t('components.documentsUpload.fileTooLarge', {
        size: fileSizeInMB.toFixed(2),
        max: maxFileSizeMB,
      });
      setError(errorMessage);

      // Call the optional error callback
      if (onFileSizeError) {
        onFileSizeError(file, maxFileSizeMB);
      }
      return false;
    }
    return true;
  };

  const validateFileType = (file: File): boolean => {
    const fileName = file.name.toLowerCase();

    const isValidType = allowedFileExtensions.some((ext) =>
      fileName.endsWith(ext),
    );

    if (!isValidType) {
      const allowedTypesText = allowedFileExtensions
        .map((ext) => ext.replace(".", "").toUpperCase())
        .join(", ");
      setError(t('components.documentsUpload.invalidFileType', { types: allowedTypesText }));
      return false;
    }
    return true;
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file && onFileUpload) {
      if (validateFileType(file) && validateFileSize(file)) {
        // Check for duplicate name
        const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
        const isDuplicate = existingFileNames.some(
          (existingName) =>
            existingName.toLowerCase() === fileNameWithoutExt.toLowerCase(),
        );

        if (isDuplicate && onDuplicateName) {
          // Handle duplicate name through callback
          onDuplicateName(
            fileNameWithoutExt,
            () => {
              // Replace: proceed with upload
              onFileUpload(file);
              setError(null);
            },
            () => {
              // Continue: proceed with upload (same name)
              onFileUpload(file);
              setError(null);
            },
            (newName: string) => {
              // Rename: create a new file with the new name
              const fileExtension = file.name.split(".").pop();
              const renamedFile = new File(
                [file],
                `${newName}.${fileExtension}`,
                { type: file.type },
              );
              onFileUpload(renamedFile);
              setError(null);
            },
          );
        } else {
          // No duplicate, proceed normally
          onFileUpload(file);
          setError(null);
        }
      }
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    if (!isCompanySelected && !isConversionTypeSelected) {
      onValidationError?.(
        t('components.documentsUpload.selectCompanyAndConversionType'),
      );
      return;
    }
    if (!isCompanySelected) {
      onValidationError?.(
        t('components.documentsUpload.selectCompany'),
      );
      return;
    }
    if (!isConversionTypeSelected) {
      onValidationError?.(
        t('components.documentsUpload.selectConversionType'),
      );
      return;
    }
    // Clear errors when user tries to upload again
    setError(null);
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    if (onFileRemove) {
      onFileRemove();
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Validate company and conversion type selection first
    if (!isCompanySelected && !isConversionTypeSelected) {
      onValidationError?.(
        t('components.documentsUpload.selectCompanyAndConversionType'),
      );
      return;
    }
    if (!isCompanySelected) {
      onValidationError?.(
        t('components.documentsUpload.selectCompany'),
      );
      return;
    }
    if (!isConversionTypeSelected) {
      onValidationError?.(
        t('components.documentsUpload.selectConversionType'),
      );
      return;
    }

    const files = e.dataTransfer.files;
    if (files.length > 0 && onFileUpload && !disabled) {
      const file = files[0];
      if (validateFileType(file) && validateFileSize(file)) {
        // Check for duplicate name
        const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
        const isDuplicate = existingFileNames.some(
          (existingName) =>
            existingName.toLowerCase() === fileNameWithoutExt.toLowerCase(),
        );

        if (isDuplicate && onDuplicateName) {
          // Handle duplicate name through callback
          onDuplicateName(
            fileNameWithoutExt,
            () => {
              // Replace: proceed with upload
              onFileUpload(file);
              setError(null);
            },
            () => {
              // Continue: proceed with upload (same name)
              onFileUpload(file);
              setError(null);
            },
            (newName: string) => {
              // Rename: create a new file with the new name
              const fileExtension = file.name.split(".").pop();
              const renamedFile = new File(
                [file],
                `${newName}.${fileExtension}`,
                { type: file.type },
              );
              onFileUpload(renamedFile);
              setError(null);
            },
          );
        } else {
          // No duplicate, proceed normally
          onFileUpload(file);
          setError(null);
        }
      }
    }
  };

  return (
    <Box
      sx={{
        p: isSmallScreen ? undefined : 3,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        borderRadius: isSmallScreen ? 0 : `${RADIUS.card}px`,
        backgroundColor: isSmallScreen ? undefined : theme.palette.brand.navy,
        boxShadow: isSmallScreen ? undefined : theme.customShadows.navy,
        height: "auto",
        maxHeight: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          flexShrink: 0,
          fontSize: "18px",
          fontWeight: 800,
          color: theme.palette.brand.cyanHead,
          mb: 1,
        }}
      >
        {heading}
      </Typography>
      <Box
        display="flex"
        flexDirection="column"
        pt={1}
        gap={2.5}
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {middleContent}
        {/* Error Display */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              mb: 1,
              "& .MuiAlert-message": {
                fontSize: "0.875rem",
              },
            }}
          >
            {error}
          </Alert>
        )}
        {/* File Upload Section */}
        {showDocuments && onFileUpload && !hasFile && (
          <Box
            onClick={handleUploadClick}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: `2px dashed ${theme.palette.common.whiteTransparent[24]}`,
              bgcolor: alpha(theme.palette.common.white, 0.05),
              borderRadius: "12px",
              py: 2.5,
              px: 2,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              minHeight: 80,
              transition: "all 0.2s ease-in-out",
              "&:hover": disabled
                ? {}
                : {
                    border: `2px dashed ${theme.palette.primary.main}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
            }}
          >
            <Icon
              icon="eva:cloud-upload-fill"
              fontSize={38}
              color={alpha(theme.palette.common.white, 0.45)}
              style={{ marginBottom: "8px" }}
            />

            <Typography
              variant="body2"
              color={alpha(theme.palette.common.white, 0.85)}
              sx={{ fontWeight: 600, transition: "color 0.2s ease-in-out" }}
            >
              {t('components.documentsUpload.uploadFile')}
            </Typography>
            <Typography
              variant="caption"
              color={alpha(theme.palette.common.white, 0.5)}
              sx={{ mt: 0.5, display: "block", textAlign: "center" }}
            >
              {t('components.documentsUpload.dragDropHint')}
              <br />
              {t('components.documentsUpload.maxFileSize', { size: maxFileSizeMB })}
            </Typography>
          </Box>
        )}
        {/* Hidden File Input */}
        {showDocuments && (
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            accept={allowedFileExtensions.join(",")}
            style={{ display: "none" }}
          />
        )}
        {showDocuments && hasFile && (
          <Paper
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              borderRadius: "12px",
              backgroundColor: theme.palette.common.white,
              border: `1px solid ${theme.palette.border.subtle}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
              <FileTypeIcon kind={docKindFor(fileName)} size={40} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body1"
                  noWrap
                  sx={{ color: theme.palette.text.tertiary }}
                >
                  {displayFileName(fileName)}
                </Typography>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {`${fileLabel} • ${fileSize} • ${fileUploadDate}`}
                </Typography>
              </Box>
            </Box>
            <Box display={"flex"} flexDirection={"row"} gap={1} flexShrink={0}>
              {/* Destructive: soft error appearance keeps it from reading as a
                  peer of the neutral action next to it (UI/UX audit 2.3.3). */}
              <Tooltip title={t('components.documentsUpload.removeFile')}>
                <span>
                  <Fab
                    size="small"
                    color="default"
                    variant="circular"
                    appearance="soft"
                    onClick={handleRemoveFile}
                    disabled={disabled}
                    aria-label={t('components.documentsUpload.removeFile')}
                    title={t('components.documentsUpload.removeFile')}
                    sx={{
                      color: theme => theme.palette.primary.dark
                    }}
                  >
                    <UiIcon name="trash" />
                  </Fab>
                </span>
              </Tooltip>
              {/* <Tooltip title="Bestand vervangen">
                <span>
                  <Fab
                    size="small"
                    color="default"
                    variant="circular"
                    appearance="soft"
                    aria-label="Bestand vervangen"
                    title="Bestand vervangen"
                    sx={{ backgroundColor: theme.palette.border.soft }}
                  >
                    <UiIcon name="refresh" />
                  </Fab>
                </span>
              </Tooltip> */}
            </Box>
          </Paper>
        )}
      </Box>
      {/* Button at the bottom — extra top margin to reach 20px gap from upload zone */}
      {showButton && (
        <Box sx={{ flexShrink: 0, mt: 1.5 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onClick}
            //onClick={() => setDuplicateNameDialogOpen(true)}
            fullWidth
            startIcon={buttonStartIcon}
            endIcon={buttonEndIcon}
            disabled={disabled}
          >
            {buttonText}
          </Button>
        </Box>
      )}
      <DuplicateNameDialog
        open={duplicateNameDialogOpen}
        onClose={() => setDuplicateNameDialogOpen(false)}
        onReplace={() => {}}
        onContinue={() => {}}
        onRename={() => {}}
        existingName={fileName || ""}
        newName={fileName || ""}
      />
    </Box>
  );
};

export default DocumentsUpload;
